import React, { useEffect, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Loading from '../loading/Loading'
import Pagination from '@mui/material/Pagination'

import ResourceSearchControls from '../search/ResourceSearchControls'

import { initializeTags } from '../../reducers/tagsReducers'
import { setErrorNotification } from '../../reducers/notificationReducer'
import { buildSearchQuery } from '../../services/utils.js/query'
import { SearchStyle } from '../styles'

// Default filter: staff sees only approved resources, others see all
const defaultFilterResources = (items, currentUser) =>
    (currentUser && currentUser.staff) ? items.filter(r => r.approved) : items

/**
 * Reusable search/browse page for paginated resource types.
 *
 * Props:
 *   pageTitle        – Heading to display at the top of the page
 *   pagePath         – React Router path for this page (e.g. PAGES.books.path)
 *   stateKey         – Redux state key for the resource list (e.g. 'books')
 *   initializeAction – Thunk creator that loads the first page of resources
 *   searchAction     – Thunk creator that performs a filtered search
 *   CardComponent    – Card component for grid view
 *   ListComponent    – List component for list view
 *   extraFilters     – Optional JSX rendered between the search controls and the results
 *   filterResources  – Optional function (resources, user) => filtered resources.
 *                      Wrap with useCallback in the parent to trigger re-filtering when
 *                      the filter criteria change (e.g. Papers' paper type checkboxes).
 *                      Defaults to filtering by the `approved` field for staff users.
 *   sortOptions      – Optional array of { value, label } for the sort dropdown.
 *                      Passed through to ResourceSearchControls.
 *   ModalComponent   – Optional modal component used for deep-linked resource URLs (e.g. /books/:id).
 *                      Required when the page is registered with an :id route parameter.
 *   fetchResource    – Optional async function (id) => resource data used with ModalComponent.
 */
const ResourceSearchPage = ({
    pageTitle,
    pagePath,
    stateKey,
    initializeAction,
    searchAction,
    CardComponent,
    ListComponent,
    extraFilters,
    filterResources,
    sortOptions,
    ModalComponent,
    fetchResource,
}) => {
    const { id: openResourceId } = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [resourcesToShow, setResourcesToShow] = useState(null)
    const [list, setList] = useState(true)
    const [sortRes, setSortRes] = useState('title')

    const [start, setStart] = useState('')
    const [end, setEnd] = useState('')
    const [title, setTitle] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])

    const [linkedResource, setLinkedResource] = useState(null)

    const user = useSelector(state => state.user)
    const resourceData = useSelector(state => state[stateKey])
    const resources = useMemo(() => resourceData?.results || [], [resourceData?.results])
    const totalPages = resourceData?.total_pages || 1
    const isLoading = resourceData?.loading || false
    const tagsByType = useSelector(state => state.tags || {})
    const dispatch = useDispatch()

    const [page, setPage] = useState(1)

    const applyFilter = filterResources || defaultFilterResources

    // Initialize tag lists needed by the search controls.
    useEffect(() => {
        if (tagsByType?.content === undefined) dispatch(initializeTags('content'))
        if (tagsByType?.medicalSpecialty === undefined) dispatch(initializeTags('medicalSpecialty'))
    }, [dispatch, tagsByType])

    // Fetch the single resource when navigating to a deep-link URL (e.g. /books/123).
    useEffect(() => {
        let isActive = true

        if (openResourceId && fetchResource) {
            fetchResource(openResourceId)
                .then(data => {
                    if (!isActive) return
                    setLinkedResource(data)
                })
                .catch(() => {
                    if (!isActive) return
                    dispatch(setErrorNotification('Resource not found'))
                    navigate(pagePath, { replace: true })
                })
        } else {
            setLinkedResource(null)
        }

        return () => {
            isActive = false
        }
    }, [openResourceId, fetchResource, dispatch, navigate, pagePath])

    useEffect(() => {
        if (searchParams.toString()) {
            const contentTagsParam = searchParams.get('content_tags')
            const medicalTagsParam = searchParams.get('medical_tags')
            const startValue = searchParams.get('start_date')
            const endValue = searchParams.get('end_date')
            const titleValue = searchParams.get('title')
            const orderingValue = searchParams.get('ordering')
            const pageValue = parseInt(searchParams.get('page')) || 1

            if (startValue) setStart(startValue)
            if (endValue) setEnd(endValue)
            if (titleValue) setTitle(titleValue)
            if (orderingValue) setSortRes(orderingValue)
            if (contentTagsParam) {
                setContentTags(contentTagsParam.split(',').filter(t => t))
            } else {
                setContentTags([])
            }
            if (medicalTagsParam) {
                setMedicalTags(medicalTagsParam.split(',').filter(t => t))
            } else {
                setMedicalTags([])
            }
            setPage(pageValue)

            // The backend matches a tag name across every typed tag field, so the
            // two URL params are combined into a single `tags` clause for the API call.
            const combinedTags = [contentTagsParam, medicalTagsParam].filter(Boolean).join(',') || null
            const query = buildSearchQuery(startValue, endValue, titleValue, combinedTags, orderingValue)
            dispatch(searchAction(query, pageValue))
        } else {
            const pageValue = parseInt(searchParams.get('page')) || 1
            setPage(pageValue)
            setContentTags([])
            setMedicalTags([])
            dispatch(initializeAction(pageValue))
        }
    }, [searchParams, user, dispatch, searchAction, initializeAction])

    useEffect(() => {
        if (resources && resources.length > 0) {
            setResourcesToShow(applyFilter(resources, user))
        } else if (!isLoading) {
            setResourcesToShow([])
        }
    }, [resources, user, isLoading, applyFilter])

    const handleSort = (event) => {
        const orderingValue = event.target.value
        setSortRes(orderingValue)
        const currentParams = new URLSearchParams(searchParams.toString())
        currentParams.set('ordering', orderingValue)
        currentParams.set('page', '1')
        navigate(`${pagePath}?${currentParams.toString()}`)
    }

    const handleSubmit = (event) => {
        if (event && event.preventDefault) event.preventDefault()

        const orderingValue = searchParams.get('ordering')

        const nextParams = new URLSearchParams()
        if (start) nextParams.set('start_date', start)
        if (end) nextParams.set('end_date', end)
        if (title) nextParams.set('title', title)
        if (contentTags && contentTags.length > 0) nextParams.set('content_tags', contentTags.join(','))
        if (medicalTags && medicalTags.length > 0) nextParams.set('medical_tags', medicalTags.join(','))
        if (orderingValue) nextParams.set('ordering', orderingValue)

        // Compare against the current params (ignoring pagination) so an unchanged
        // search doesn't trigger a redundant navigation. Sort both since the entry
        // points that mutate the URL don't guarantee a stable param order.
        const currentParams = new URLSearchParams(searchParams.toString())
        currentParams.delete('page')
        const normalizedNext = new URLSearchParams(nextParams.toString())
        normalizedNext.sort()
        currentParams.sort()

        if (normalizedNext.toString() !== currentParams.toString()) {
            navigate(`${pagePath}?${nextParams.toString()}`)
        }
    }

    const handlePageChange = (event, value) => {
        const currentParams = new URLSearchParams(searchParams.toString())
        currentParams.set('page', value.toString())
        navigate(`${pagePath}?${currentParams.toString()}`)
    }

    return (
        <Container sx={SearchStyle.container}>
            <Typography {...SearchStyle.title} gutterBottom>
                {pageTitle}
            </Typography>

            <ResourceSearchControls
                title={title}
                setTitle={setTitle}
                start={start}
                setStart={setStart}
                end={end}
                setEnd={setEnd}
                contentTags={contentTags}
                setContentTags={setContentTags}
                medicalTags={medicalTags}
                setMedicalTags={setMedicalTags}
                isLoading={isLoading}
                onSubmit={handleSubmit}
                sortRes={sortRes}
                onSort={handleSort}
                list={list}
                setList={setList}
                sortOptions={sortOptions}
            />

            {extraFilters}

            {linkedResource && ModalComponent && (
                <ModalComponent
                    {...linkedResource}
                    initialOpen={true}
                    onExternalClose={() => navigate(pagePath, { replace: true })}
                />
            )}

            {isLoading && <Loading />}
            {!isLoading && (
                (resourcesToShow && resourcesToShow.length > 0) ? (
                    <>
                        <Grid container spacing={2}>
                            {list
                                ? resourcesToShow.map(resource => (
                                    <Grid key={resource.id} item xs={12}>
                                        <ListComponent {...resource} />
                                    </Grid>
                                ))
                                : resourcesToShow.map(resource => (
                                    <Grid key={resource.id} item xs={12} md={4} sx={SearchStyle.card}>
                                        <CardComponent {...resource} />
                                    </Grid>
                                ))
                            }
                        </Grid>
                        <Pagination sx={SearchStyle.pagination} count={totalPages} page={page} onChange={handlePageChange} />
                    </>
                ) : (
                    <Typography {...SearchStyle.noResources.props} gutterBottom sx={SearchStyle.noResources.style}>
                        No Resources Found
                    </Typography>
                )
            )}
            <Box sx={{ textAlign: 'center', padding: '10px' }}>
                <Typography variant='overline' component='p'>
                    All resources provided are strictly owned and distributed by the creators indicated on the resource
                </Typography>
            </Box>
        </Container>
    )
}

export default ResourceSearchPage
